import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { CompetencyBarsSlideout } from '../../../adl-gen/totalinteraction/futurefit/db';
import {
  SingleUserQuizResults,
  SingleUserTrainingRecommendations
} from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { TranslateFn, withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { QuizResultsHelper } from '../../../stores/quiz-results-store';
import { CompetencyBars } from '../../components/competency-bars/competency-bars';
import { SkillGroupBars } from '../../components/competency-bars/group-bars';
import { CompetencyRadar } from '../../components/competency-radar/competency-radar';
import { PlotKeyLegend } from '../../components/plot-key-legend/plot-key-legend';
import { SkillGroupRadar } from '../../components/skill-group-radar/skill-group-radar';
import { StringKeyMap } from '../../../adl-gen/common';

const styles = require('./quiz-summary.css');

interface QuizSummaryProps extends WithI18nProps {
  qr: QuizResultsHelper;
  plotKeyToggleState: PlotKeyToggleState;
  displayHeadlineRatio: boolean;
  displayHeadlineText?: boolean;
}

export interface LoadedProps {
  /** Data response object with all quiz results and the skills competencies model */
  quizResults: SingleUserQuizResults;
  userQuizTrainingOfferings: SingleUserTrainingRecommendations;
}

@observer
class QuizSummary0 extends React.Component<QuizSummaryProps> {
  @observable
  // this is an ID
  selectedCompetency: string | undefined = undefined;
  // this is just a string
  @observable
  selectedGroup: string | undefined = undefined;
  @observable
  lastSelectedCompetency: string | undefined = undefined;
  @observable
  lastSelectedGroup: string | undefined = undefined;

  @action
  selectCompetency = (selected: string): void => {
    if (this.props.qr.quiz.competencyBars !== CompetencyBarsSlideout.none) {
      this.selectedCompetency = this.selectedCompetency === selected ? undefined : selected;
      if (this.selectedCompetency !== undefined) {
        this.lastSelectedCompetency = this.selectedCompetency;
      }
    }
  };

  @action
  selectGroup = (heading: string): void => {
    // Ignore CompetencyBarsSlideout setting
    this.selectedGroup = this.selectedGroup === heading ? undefined : heading;
    if (this.selectedGroup !== undefined) {
      this.lastSelectedGroup = this.selectedGroup;
    }
  };

  setRadii = (qr: QuizResultsHelper): number[] => {
    /// Return default if # ranges is < 1 or > 5
    const defaultRadii = [20, 40, 60, 80, 100];
    /// Copy ranges, map to just max values, sort
    const ranges = qr.ranges
      .slice()
      .map(r => r.scoreRange.range.v2)
      .sort((a, b) => a - b);
    if (ranges.length < 1 || ranges.length > 5) {
      return defaultRadii;
    } else {
      /// Find their gaps as percent; set ranges accordingly
      const highestMax = ranges[ranges.length - 1];
      return ranges.map(r => Math.floor((r / highestMax) * 100));
    }
  };

  renderCompetencyContent(qr: QuizResultsHelper): JSX.Element {
    /// Display headline explanatory text: only if
    /// - prop is not undefined and set to TRUE
    /// - displayHeadlineRatio is OFF
    /// - role comparisons are not active
    /// and ignore all this for Skill Groups
    const roleComparisonsActive = qr.isRoleSelected();
    const displayHeadlineText =
      !this.props.displayHeadlineRatio && ((!roleComparisonsActive && this.props.displayHeadlineText) || false);

    const infoIncluded = this.props.displayHeadlineRatio || displayHeadlineText;
    const radarStyle = infoIncluded ? styles.radarContainer : styles.radarContainer2;

    return (
      <div className={styles.content}>
        {this.props.displayHeadlineRatio ? (
          <div className={this.slideIf(this.selectedCompetency === undefined, styles.infoContainer)}>
            {renderQuizInfo(qr, this.props.t)}
          </div>
        ) : displayHeadlineText ? (
          <div className={this.slideIf(this.selectedCompetency === undefined, styles.infoContainer)}>
            {renderExplanatoryText(qr, this.props.t)}
          </div>
        ) : null}
        <div className={this.slideIf(this.selectedCompetency === undefined, radarStyle)}>
          <CompetencyRadar
            qr={qr}
            plotKeyToggleState={this.props.plotKeyToggleState}
            onCompetencyClick={this.selectCompetency}
            selectedCompetency={this.selectedCompetency}
            /// TODO this is easy to drop in but may look rubbish
            gridRadii={this.setRadii(qr)}
          />
        </div>
        <div className={this.slideIf(this.selectedCompetency === undefined, styles.barContainer)}>
          {this.lastSelectedCompetency !== undefined && (
            <CompetencyBars
              competencyId={this.lastSelectedCompetency}
              plotKeyToggleState={this.props.plotKeyToggleState}
              qr={qr}
            />
          )}
        </div>
      </div>
    );
  }


  renderSkillGroupContent(qr: QuizResultsHelper): JSX.Element {
    return (
      <div className={styles.content}>
        <div className={this.slideIf(this.selectedGroup === undefined, styles.radarContainer2)}>
          <SkillGroupRadar
            qr={qr}
            plotKeyToggleState={this.props.plotKeyToggleState}
            selectedGroup={this.selectedGroup}
            gridRadii={this.setRadii(qr)}
            onGroupClick={this.selectGroup}
          />
        </div>
        <div className={this.slideIf(this.selectedGroup === undefined, styles.barContainer)}>
          {this.renderSkillGroupBars(qr, this.lastSelectedGroup)}
        </div>
      </div>
    );
  }
  renderSkillGroupBars(qr: QuizResultsHelper, selectedGroup: string | undefined): JSX.Element | null {
    if (selectedGroup === undefined) return null;
    const skillGroups = qr.getSkillGroups();
    if (skillGroups === undefined) return null;
    const skillIds = skillGroups[selectedGroup];
    if (skillIds.length === 0) return null;
    return (
      <SkillGroupBars
        skillGroup={{ heading: selectedGroup, skillIds }}
        plotKeyToggleState={this.props.plotKeyToggleState}
        qr={qr}
      />
    )
  }

  slideIf(condition: boolean, className: string) {
    if (!condition) {
      return classnames(className, styles.animate, styles.toSlide);
    } else {
      return classnames(className, styles.animate);
    }
  }

  hasSkillGroups(groups: StringKeyMap<string, string[]> | undefined): boolean {
    return !(groups === undefined) && !(Object.keys(groups).length === 0);
  }

  render() {
    const qr = this.props.qr;
    const role = qr.quiz.workRole;
    return (
      <div className={styles.report}>
        <div className={styles.legendContainer}>
          <PlotKeyLegend
            qr={qr}
            plotKeyToggleState={this.props.plotKeyToggleState}
            displayHeadlineRatio={this.props.displayHeadlineRatio}
            displayHeadlineText={this.props.displayHeadlineText}
          />
        </div>
        {(role.useSkillGroupReport && this.hasSkillGroups(qr.getSkillGroups()))
          ? this.renderSkillGroupContent(qr)
          : this.renderCompetencyContent(qr)}
      </div>
    );
  }
}

export const QuizSummary = withI18n(QuizSummary0);

// TODO: triage warning "Functions not valid with react child"
/// FF2-315: See definition of scoreHeadlineRatio()
/// FF2-436: Stick in div and add walkthrough data-tour attribute support
export function renderQuizInfo(qr: QuizResultsHelper, t: TranslateFn): JSX.Element {
  const text = 'towards desired skill level for the ' + t(qr.quiz.workRole.name) + ' role.';
  const i15dTextBefore = i18next.t('headline-before', { roleName: t(qr.quiz.workRole.name) });
  const i15dTextAfter = i18next.t('headline-after', { roleName: t(qr.quiz.workRole.name), defaultValue: text });
  // const text = (qr.areRolesSelected())
  //     ? "the " + qr.quiz.workRole.name + " role"
  //     : "this role";
  return (
    <div data-tour={'quiz-summary'}>
      <h1 className={styles.quizScoreBlurb}>
        {i15dTextBefore} <b className={styles.quizScoreHighlight}>{qr.scoreHeadlineRatioText()}%</b> {i15dTextAfter}
      </h1>
    </div>
  );
}

export function renderExplanatoryText(qr: QuizResultsHelper, t: TranslateFn): JSX.Element {
  const text =
    '% values compare assessed capabilities with those desired for the ' + t(qr.quiz.workRole.name) + ' role';
  const i15dText = i18next.t('percent-explanatory', { roleName: t(qr.quiz.workRole.name), defaultValue: text });
  return (
    <div data-tour={'quiz-explanatory'}>
      <h1 className={styles.quizScoreBlurb}>{i15dText}</h1>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/ui/screens/quiz-summary/quiz-summary.tsx